// react and context
import { useEffect, useState, useContext, useRef } from 'react'
import { IntakeContext } from '../context/IntakeContext'

// mui and icons
import {
  IconButton,
  Tooltip,
  Fade,
  Box,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'
import financeImage from 'assets/images/finance-image.svg'
import { LoginModal } from './LoginModal'

// custom components
import { OnboardingTile } from './OnboardingTile'

const OnboardingMultiOptionTile = ({
  id,
  onChange = (e) => console.log(e),
  preHeader,
  header,
  subHeader,
  helpText,
  expectedDataType,
  tileSize,
  optionArray,
  onTileClick,
  handleNext,
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const [tileValue, setTileValue] = useState(intakeState?.[id])
  const [open, setOpen] = useState(false)

  const userInitiatedChange = useRef(false)

  const handleClose = () => setOpen(false)

  const toggleTileValue = (name) => {
    //if a tile is re-selected process the same way
    if (tileValue === name) {
      onChange(name)
      if (typeof name === expectedDataType) {
        updateIntakeStateAtKey({ [id]: name })
        handleNext?.()
      }
      return
    }
    setTileValue(name)
    userInitiatedChange.current = true
    onTileClick?.()
  }

  useEffect(() => {
    onChange(tileValue)
    if (typeof tileValue != expectedDataType) return
    updateIntakeStateAtKey({ [id]: tileValue })
  }, [tileValue])

  useEffect(() => {
    if (
      userInitiatedChange.current &&
      intakeState?.[id] === tileValue &&
      typeof handleNext === 'function'
    ) {
      handleNext()
      userInitiatedChange.current = false
    }
  }, [intakeState?.[id]])

  const fieldMap = (data) => {
    return (
      <Grid container spacing={1} justifyContent="center" mb={3}>
        {data.map((question) => (
          <Grid item {...tileSize} key={question.optionValue}>
            <OnboardingTile
              label={question.optionLabel}
              icon={question.optionIcon}
              imageURL={question.optionImageURL}
              active={tileValue === question.optionValue}
              onClick={() => toggleTileValue(question.optionValue)}
              className={question.className}
              optionDescription={question.optionDescription}
              optionTimer={question.optionTimer}
              optionLabelBadge={question.optionLabelBadge}
              optionLabelBadgeType={question.optionLabelBadgeType}
              optionAdditionalContent={question.optionAdditionalContent}
              optionTimerClass={question.optionTimerClass}
              optionTimerIcon={question.optionTimerIcon}
              optionLink={question.optionLink}
              arrowIndicator={question.arrowIndicator}
            ></OnboardingTile>
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        sx={{
          '.MuiPaper-root': {
            padding: '24px',
            borderRadius: '8px',
          },
        }}
      >
        <img
          src={financeImage}
          style={{ maxWidth: '400px', display: 'block', margin: 'auto' }}
        />
        <DialogTitle
          sx={{ fontSize: '24px', paddingBottom: '8px' }}
          id="alert-dialog-title"
          textAlign="center"
        >
          {'Your PARCO account has been created!'}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            textAlign="center"
            id="alert-dialog-description"
          >
            To complete your profile, continue with onboarding.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            variant="contained"
            sx={[
              {
                fontSize: '15px',
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
                textTransform: 'none',
              },
              {
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
              },
            ]}
            onClick={handleClose}
            autoFocus
          >
            Continue with Onboarding
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ textAlign: 'left' }}>
        {preHeader !== false && (
          <Box>
            <Typography
              variant="subtitle1"
              fontSize={{ xs: '16px', sm: '20px' }}
            >
              {preHeader || ''}
            </Typography>
          </Box>
        )}

        {header !== false && (
          <Box mb={1}>
            <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
              {header || "Let's start with your basic information:"}
              {helpText !== '' ? (
                <Tooltip
                  TransitionComponent={Fade}
                  title={
                    helpText ||
                    'This helps us calculate your retirement benefits'
                  }
                  placement="top"
                  color="info"
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                >
                  <IconButton size="small" color="dark">
                    <HelpIcon sx={{ marginTop: '-6px' }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <></>
              )}
            </Typography>
          </Box>
        )}

        {subHeader !== false && (
          <Typography
            variant="body1"
            sx={{ lineHeight: '1.2' }}
            fontSize={{ xs: '16px', sm: '20px' }}
          >
            {subHeader || 'Let us know basic details about you!'}
          </Typography>
        )}
        <Box my={header !== false ? 4 : 0} mb={header !== false ? 4 : 1}>
          {fieldMap(optionArray)}
        </Box>
      </Box>
    </>
  )
}

export default OnboardingMultiOptionTile
